/**
 * Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
 * analytics events keep the historical "owner note" name on purpose — do not rename them.
 *
 * One pick's owner surface on a best-list article (plan: docs/owner-notes-guide-funnel-plan.md
 * §3 WP3; design brief v4, 2026-09-15; one row, one sheet, one seat — 2026-10-17).
 *
 *  - `variant: 'roster'` follows its `nc-deals-detailed` row; `variant: 'section'` follows its
 *    in-depth card (`nc-deals-large` or `-alt`). Both render the SAME block: the action row —
 *    Pin to compare, Measurements, Owners — then what the row opens; a declared in-depth block
 *    heads it with the verdict sentence. WHAT differs per placement is one table,
 *    `strip-rules.ts` (Guillaume, 2026-10-21: every placement reads the same, one place to
 *    change it) — nothing here branches on the placement except through it. The score chips
 *    left every strip that day (the numbers keep flowing; the phone sheet's "This pick" still
 *    shows them). Three fixed slots in one order on every
 *    surface a pick has (Guillaume, 2026-10-17: on a phone the four features had three
 *    vocabularies and two places each). A slot the pick cannot honour is greyed, never removed.
 *    The "Our picks" grid card of the new best-list style carries the same row, compact
 *    (`GuideGridPick`, the card's `footerSlot`): glyphs and the count, and both buttons open the
 *    sheet at every width — the card has no room to expand.
 *  - Desktop (`lg` and up, 2026-10-18): Measurements and Owners open the page's one side panel
 *    (`LabCompareDrawer`, `lab.focusPick`) on this pick — "This pick" / "Owners" beside the
 *    pinned "Compare" — the grid card and the in-depth card alike; nothing expands inline and
 *    nothing dims the page. No strip prints the reviews in place any more (Guillaume,
 *    2026-10-21 — until then the roster row and a sections-only pick did, and the new style
 *    closed each review with them): the strip serves the SAME Owners block `hidden` for
 *    crawlers, the page's one HTML copy of those reviews — the roster row's strip when the
 *    pick has one, else its section strip (`hasRoster`). The hidden copy mounts no view beacon.
 *  - Where the section strip sits (2026-10-21): INSIDE its `nc-deals-large` card, as the card's
 *    footer (`inCard` — `NCDealsLargeSG` renders it into the shared card's `footerSlot`), or
 *    the block after an `nc-deals-large-alt`, a box of its own. On a news post or a review post
 *    the same strip renders under `CatalogAnalyticsProvider`'s `post_page` / `review_page`
 *    scope (their templates mount the lab surfaces too); the seated ask is a guide's alone.
 *  - Phone (below `lg`): either button opens the pick's sheet (`PickSheet`): the panel's three
 *    segments — This pick, Owners, Compare (the pinned comparison, inside the sheet) — and the
 *    pin alone in its foot. The roster row's inline owners block stays in the HTML — the
 *    server serves each note once, for every visitor (#51, SEO audit 2026-09-18) — and is
 *    the sheet prints the pick's reviews on its Owners segment.
 *  - Undeclared, a chip is the pick's value on its scale ("4.9 /5") and the panel draws the
 *    pick alone (the edge-cached render). Declared, the chips read "vs yours" and the reader's
 *    curve lands on the panel in orange.
 *  - The pick the reader owns: no chips, "You own this", its own curves alone, and — on its
 *    roster row, or on its in-depth block when it has no row — the ONE owner-review ask on the
 *    page (`OwnedAsk`): the form's own buy-again question, Yes / No, which opens the band right
 *    here (`GuideCapture` hosts it and portals it into this block's slot over the bus). The
 *    reference strip at the top points at this ask and asks nothing itself.
 *
 * What it does NOT own: the row and the card above it (shared `NCDealsDetailed` / `NCDealsLarge`,
 * never edited — the roster block attaches to the row visually instead), the band (`GuideCapture`
 * hosts it; this block lends the slot and asks for it over the bus), and every comparison rule,
 * which lives in `lib/owner-notes/{deltas,verdict}.ts`.
 */
import { useEffect, useMemo, useRef, useState, type RefObject } from 'react'
import clsx from 'clsx'
import { ResponsiveContext } from 'shared-components/contexts/ResponsiveContext'
import styles from './GuidePick.module.css'
import PickOwners from './PickOwners'
import PickSheet, { type PickSheetSegment } from './PickSheet'
import DeclaredFingerprint from '../DeclaredFingerprint'
import OwnNoteCard from '../OwnNoteCard'
import OwnerExample from './OwnerExample'
import PinToCompare, { pinLabel } from '../PinToCompare'
import type { DSBest } from 'shared-definitions/types'
import type { Point } from 'lib/dealhunt/audioChart'
import { useOnScreenOnce, useOwnerTrack, type OwnerTrack } from 'lib/owner-notes/analytics'
import {
  BAND_CTA,
  DRAFT_WAITING_CTA,
  EXAMPLE_TOGGLE,
  FORM_COST_LINE,
  GATED_FORM_COST_LINE,
  GUIDE_ASKS,
  GUIDE_COPY,
  ownerAskVisible,
  ownersFromCard,
  payoffLine,
  tileHeadline,
} from 'lib/owner-notes/band-copy'
import {
  publishSeatedSurface,
  requestOpenOwnerBand,
  useOwnNoteSignal,
  useSeatedOwner,
} from 'lib/owner-notes/bus'
import { useWriterHandoff } from 'lib/community/use-writer-handoff'
import { signInGateFor } from 'lib/owner-notes/signin-gate'
import { useBandDraftWaiting } from 'lib/owner-notes/draft-waiting'
import HandoffHint from 'components/owner-notes/HandoffHint'
import Linked from 'shared-components/service/Linked'
import { cardOwnersFromCard } from 'lib/owner-notes/card-owners'
import { THIS_COLOR, YOURS_COLOR, useCurves, useInView } from 'lib/owner-notes/curves'
import { rosterChips } from 'lib/owner-notes/roster-chips'
import { useDeclaredContext, type DeclaredProduct } from 'lib/owner-notes/declared'
import { useLabCompare } from 'lib/owner-notes/lab-compare'
import {
  bandVerdicts,
  compareRows,
  labTested,
  type BandVerdict,
  type Comparison,
  type DeltaNumbers,
} from 'lib/owner-notes/deltas'
import { isDeclaredPick, pickNumbers, wantsCurves } from 'lib/owner-notes/guide-pick'
import { useOwnNote } from 'lib/owner-notes/own-note'
import { turnstileConfigured } from 'lib/owner-notes/turnstile'
import { buildVerdict, widestBand, type VerdictClause } from 'lib/owner-notes/verdict'
import type { DSGuidePickRef, DSNCOwnerGuidePick } from 'types.sg.ds'
import { useCatalogEvent } from 'lib/analytics/catalog'
import { stripRenders, stripRules, type StripRules } from './strip-rules'

/** The seat's id: the reference strip's link target. One seat per page, so one id. */
export const OWNER_ASK_ID = 'guide-owner-ask'

const ACTIONS = GUIDE_COPY.actions

/** What both variants need from the pair: the comparison, the curves, and the band the verdict
 *  leads with. `enabled` is `wantsCurves` — and keeps the fetch off every row on mount: the
 *  observer arms as the row scrolls in (Directive `f80d2e70`). Without a declaration only the
 *  pick's curves load; the comparison and the verdict stay null. */
function usePairVerdict(args: {
  ref: React.RefObject<HTMLElement>
  declared: DeclaredProduct | null
  theirs: DeltaNumbers
  enabled: boolean
  rowIndex?: number
  /** How far below the viewport the block may be and still fetch (the in-view margin). */
  margin?: string
}): {
  /** The block has come within `margin` of the viewport (sticky). */
  inView: boolean
  comparison: Comparison | null
  mineCurve: Point[] | null
  theirCurve: Point[] | null
  bands: BandVerdict[]
  clauses: VerdictClause[]
} {
  const { ref, declared, theirs, enabled, rowIndex, margin } = args
  const comparison = useMemo(
    () => (declared && enabled ? compareRows(declared, theirs) : null),
    [declared, enabled, theirs]
  )
  const theirId = theirs.clearbuyProductId ?? null
  const wantCurves = enabled && theirId !== null
  const inView = useInView(ref, wantCurves, margin)
  const lab = comparison?.tier === 'lab'
  const myCurves = useCurves(declared?.clearbuyProductId ?? null, lab && inView)
  const theirCurves = useCurves(theirId, wantCurves && inView)
  const mineCurve = myCurves?.frequency?.points ?? null
  const theirCurve = theirCurves?.frequency?.points ?? null
  const bands = useMemo(
    () => (mineCurve && theirCurve ? bandVerdicts(mineCurve, theirCurve) : []),
    [mineCurve, theirCurve]
  )
  const verdict = useMemo(() => {
    if (!declared || !comparison) {
      return null
    }
    return buildVerdict({
      comparison,
      band: widestBand(bands),
      mine: declared,
      theirs,
      theirsLabTested: labTested(theirs),
      mineLabTested: labTested(declared),
      rowIndex,
    })
  }, [bands, comparison, declared, rowIndex, theirs])
  return {
    inView,
    comparison,
    mineCurve,
    theirCurve,
    bands,
    clauses: verdict?.clauses ?? [],
  }
}

/**
 * The verdict's clauses as one line (the in-depth block only, since the roster became chips
 * on 2026-09-16). Each clause and its values are one `white-space: nowrap` unit, so a narrow
 * column wraps BETWEEN clauses and never between a number and what it measures. Every pair is
 * coloured: the pick's number in `THIS_COLOR`, the reader's in `YOURS_COLOR` — the two colours
 * the block's key already uses, so "10 h vs 9 h" three clauses in still says whose 9 h that is.
 */
const Clauses: React.VFC<{
  clauses: readonly VerdictClause[]
  className: string
}> = ({ clauses, className }) => (
  <p className={className}>
    {clauses.map(clause => (
      <span key={clause.text} className={styles.clause}>
        {/* Real spaces, not only the margins: without them a screen reader runs the clause and
            its numbers together as one word ("Blocks more9.2 vs 8.6"). */}
        <span className={styles.clauseText}>{clause.text}</span>
        {clause.values && (
          <span className={styles.clauseValues}>
            {' '}
            <span className={styles.valThis} style={{ color: THIS_COLOR }}>
              {clause.values.theirs}
            </span>
            {' vs '}
            <span className={styles.valYours} style={{ color: YOURS_COLOR }}>
              {clause.values.mine}
            </span>
          </span>
        )}
        {clause.note && <span className={styles.clauseNote}>{` ${clause.note}`}</span>}
      </span>
    ))}
  </p>
)

/** One chip: the pick's value in blue, then "vs yours" and the reader's value in orange — or,
 *  before a declaration, the scale the value is on, so "4.9" is never read as out of ten. No
 *  sparkline: tried for a round on 2026-09-17 and dropped (Guillaume) — a 56px curve beside a
 *  number says nothing the number does not. */
const Chip: React.VFC<{
  label: string
  theirs: string
  mine: string | null
  max: number | null
}> = ({ label, theirs, mine, max }) => (
  <span className={styles.chip}>
    <span className={styles.chipLabel}>{label}</span>{' '}
    <span className={styles.chipThis} style={{ color: THIS_COLOR }}>
      {theirs}
    </span>
    {mine === null ? (
      max !== null && <span className={styles.chipVs}>{` /${max}`}</span>
    ) : (
      <>
        {' '}
        <span className={styles.chipVs}>{GUIDE_COPY.roster.vs}</span>{' '}
        <span className={styles.chipYours} style={{ color: YOURS_COLOR }}>
          {mine}
        </span>
      </>
    )}
  </span>
)

// ── the action row ───────────────────────────────────────────────────────────

/** The compact row's glyphs (the grid card): one spec — 14px box, 1.6 stroke, `currentColor`. */
const IconPin: React.VFC = () => (
  <svg width="14" height="14" viewBox="0 0 12 12" fill="none" aria-hidden="true" focusable="false">
    <path
      d="M7.5 1.5l3 3-1.2.4-2 2 .3 2.3L6 8.6 3.4 11 1 8.6l2.4-2.6L1.6 4.4l2.3.3 2-2z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const IconChart: React.VFC = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
    <path
      d="M1.5 11.5l3.5-4.5 3 3 3.5-5.5 3 3.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const IconOwners: React.VFC = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
    <circle cx="8" cy="5.5" r="2.6" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M2.8 14c.6-2.8 2.5-4.2 5.2-4.2s4.6 1.4 5.2 4.2"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
)

/**
 * Pin to compare · Measurements · Owners — one row, one order, one set of words, under every
 * pick (2026-10-17). The pin is `PinToCompare` itself (the drawer's own control, as a tile);
 * on the pick the reader owns, or one the lab has not measured, its slot is a greyed label so
 * the other two keep their place. Measurements and Owners are disclosure buttons: on desktop
 * they open inline under the row, on a phone the sheet — the caller decides, this row only
 * says what is open.
 */
const PickActions: React.VFC<{
  pick: DSGuidePickRef
  placement: 'roster' | 'section' | 'grid'
  yours: boolean
  measurable: boolean
  measureOpen: boolean
  /** Under "Measurements": "vs yours" once declared, else nothing. */
  measureSub: string | null
  onMeasure: () => void
  ownersCount: number
  ownersOpen: boolean
  onOwners: () => void
  /** The grid card's row: glyphs over the count, the label for screen readers. */
  compact: boolean
}> = ({
  pick,
  placement,
  yours,
  measurable,
  measureOpen,
  measureSub,
  onMeasure,
  ownersCount,
  ownersOpen,
  onOwners,
  compact,
}) => {
  const { context } = pick
  return (
    <div className={clsx(styles.actions, compact && styles.actionsCompact)}>
      {context && context.lab_tested && !yours ? (
        <PinToCompare
          nodeId={context.node_id}
          slug={context.full_slug}
          name={context.name}
          pdpHref={context.sg_href}
          clearbuyProductId={context.compare.clearbuyProductId ?? pick.clearBuyProductId}
          // Pinning is a comparison between picks; it does not wait for a declaration.
          requireDeclared={false}
          // A second click takes it back out (Guillaume, 2026-09-15).
          mode="toggle"
          location={placement}
          variant={compact ? 'icon' : 'tile'}
        />
      ) : (
        <span className={clsx(styles.action, styles.actionOff)}>
          {compact && <IconPin />}
          <span className={compact ? styles.srOnly : undefined}>{pinLabel('toggle', false)}</span>
          <small className={styles.actionSub}>
            {yours ? ACTIONS.pinYours : ACTIONS.pinUnmeasured}
          </small>
        </span>
      )}
      <button
        type="button"
        className={clsx(styles.action, measureOpen && styles.actionOn)}
        disabled={!measurable}
        aria-expanded={measurable ? measureOpen : undefined}
        onClick={onMeasure}
      >
        {compact && <IconChart />}
        <span className={compact ? styles.srOnly : undefined}>{ACTIONS.measurements}</span>
        <small className={styles.actionSub}>
          {measurable ? measureSub ?? '' : ACTIONS.pinUnmeasured}
        </small>
      </button>
      <button
        type="button"
        className={clsx(styles.action, ownersOpen && styles.actionOn)}
        disabled={ownersCount === 0}
        // Owners names no block on the page: the panel and the sheet are their own surfaces.
        aria-expanded={ownersCount > 0 ? ownersOpen : undefined}
        onClick={onOwners}
      >
        {compact && <IconOwners />}
        <span className={compact ? styles.srOnly : undefined}>{ACTIONS.owners}</span>
        <small className={styles.actionSub}>
          {ownersCount > 0 ? ACTIONS.ownersCount(ownersCount) : ACTIONS.ownersNone}
        </small>
      </button>
    </div>
  )
}

// ── the pick the reader owns: the one ask ─────────────────────────────────────

/** What the owned pick's block needs: the reader's note on it, and whether to ask. */
function useOwnedPick(
  nodeId: string,
  trackOwner: OwnerTrack,
  location: 'roster' | 'seated',
  /** The ask's element: the impression fires once it is on screen, not when it renders. */
  askRef: RefObject<HTMLElement>
): {
  own: ReturnType<typeof useOwnNote>
  askVisible: boolean
  bandOpen: boolean
  /** The capture block says this reader has a note — ahead of this block's own `/me`. */
  hasNote: boolean
} {
  // `/me` is asked for the product the reader owns, never for nine picks. (If a third surface
  // ever wants it, the fix is a cache in `own-note.ts`, not a wider bus.)
  const own = useOwnNote(nodeId || null)
  // What the capture strip knows: whether this reader has a note and whether the page's one
  // band is open right now. The ask is here and the band's owner is the strip, so this block
  // reads the strip's signal rather than only its own `/me`: one reader, one answer, and the
  // invitation goes away while they are filling the form in.
  const signal = useOwnNoteSignal()
  const askVisible = ownerAskVisible({
    configured: turnstileConfigured(),
    // Always: an owned pick needs a declaration, which is read from localStorage after
    // hydration, so this never server-renders. `hydrated: false` is the edge-cache case —
    // "show the ask, the server cannot know the reader" — and passing it here would make the
    // three checks below unreachable, which is the opposite of what they are for.
    hydrated: true,
    hasOwnNote: signal.hasNote || own.ownNote !== null,
    bandOpen: signal.bandOpen ?? false,
    ownPending: signal.pending || own.pending,
  })
  const askSeen = useOnScreenOnce(askRef, askVisible)
  const askShown = useRef(false)
  useEffect(() => {
    if (askVisible && askSeen && !askShown.current) {
      askShown.current = true
      trackOwner('ask_shown', { location })
    }
  }, [askVisible, askSeen, trackOwner, location])
  return { own, askVisible, bandOpen: signal.bandOpen ?? false, hasNote: signal.hasNote }
}

/**
 * The page's one owner-review ask, seated on the pick the reader owns — its roster row, or its
 * in-depth block when it has no row (`PickStrip` decides which). The form's own buy-again
 * question with its two answers (the tap is that answer, given early, and opens the rest of
 * the note), the cost line, the example; then their note once it exists. Under it, the slot
 * the page's one band renders into (`GuideCapture` portals it here over the bus), so the form
 * opens where the question was asked. The seat is published under this block's `owner` token:
 * no other block's `false` can clobber it, and a second matching block finds it taken.
 */
const OwnedAsk: React.VFC<{
  pick: DSGuidePickRef
  placement: 'roster' | 'section'
  owner: string
}> = ({ pick, placement, owner }) => {
  const { context } = pick
  const name = context?.name ?? pick.title
  const nodeId = context?.node_id ?? ''
  const trackOwner = useOwnerTrack({ nodeId, name })
  const ownersSummary = useMemo(() => ownersFromCard(context?.owners ?? null), [context])
  const askRef = useRef<HTMLDivElement>(null)
  const slotRef = useRef<HTMLDivElement>(null)
  const location = placement === 'roster' ? 'roster' : 'seated'
  const { own, askVisible, bandOpen, hasNote } = useOwnedPick(nodeId, trackOwner, location, askRef)
  // One Review Writer (P1): the ask links the writer for this pick's product.
  const handoff = useWriterHandoff(location, context?.sg_href, { flag: 'guide' })
  // The sign-in gate (slice D): the cost line says it; an inline ask whose band draft is waiting
  // in this tab says "Finish your owner review" (client-only, after mount).
  const gated = signInGateFor('guide')
  // Keyed as `GuideCapture`'s band writes it: the DECLARED product's id, which may be an alias of
  // this pick's node (#110).
  const declaredNodeId = useDeclaredContext()?.declared?.nodeId ?? nodeId
  const draftWaiting = useBandDraftWaiting(declaredNodeId, !handoff && gated, bandOpen)
  const [exampleOpen, setExampleOpen] = useState(false)

  // The seat is taken while the ask shows, their note shows, or the band is open in the slot —
  // and the moment after Publish, when the capture block knows the note before this block's
  // own `/me` re-read does (`hasNote`): the card lands here once it catches up, not up at the
  // strip in between.
  const seatedShown = askVisible || own.ownNote !== null || bandOpen || hasNote
  useEffect(() => {
    publishSeatedSurface(seatedShown, slotRef.current, owner)
  }, [seatedShown, owner])
  useEffect(() => () => publishSeatedSurface(false, null, owner), [owner])

  const payoff = payoffLine(ownersSummary)
  // What the seat offers: the handed-off link, the waiting draft's button, or the question.
  let offer: React.ReactNode
  if (handoff) {
    offer = (
      <>
        <Linked
          href={handoff.href()}
          className={clsx(styles.seatedCta, styles.ctaLink)}
          {...handoff.linkProps}
        >
          {BAND_CTA}
        </Linked>
        <HandoffHint text={handoff.hint} />
      </>
    )
  } else if (draftWaiting) {
    offer = (
      <button
        type="button"
        className={styles.seatedCta}
        onClick={() => requestOpenOwnerBand(location)}
      >
        {DRAFT_WAITING_CTA}
      </button>
    )
  } else {
    // The form's own first question: the tap is that answer and opens the band seeded with
    // it, right here (the band renders into the slot below).
    offer = (
      <div className={styles.seatedAsk} role="group" aria-label={GUIDE_ASKS.roster.question}>
        <p className={styles.seatedQuestion}>{GUIDE_ASKS.roster.question}</p>
        <div className={styles.inviteAnswers}>
          <button
            type="button"
            className={styles.yoursCta}
            onClick={() => requestOpenOwnerBand(location, true)}
          >
            {GUIDE_ASKS.roster.answers.yes}
          </button>
          <button
            type="button"
            className={styles.inviteNo}
            onClick={() => requestOpenOwnerBand(location, false)}
          >
            {GUIDE_ASKS.roster.answers.no}
          </button>
        </div>
      </div>
    )
  }
  return (
    <div className={styles.rowOwners}>
      {own.ownNote && !askVisible ? (
        <div className={styles.seated}>
          <OwnNoteCard
            note={own.ownNote}
            claimed={own.claimed}
            trackOwner={trackOwner}
            location="own_note"
            productHref={context?.sg_href ?? null}
            onEdit={() => requestOpenOwnerBand('own_note')}
            onDeleted={() => {
              own.applyDeleted()
            }}
            onClaimed={claim => {
              own.applyClaim(claim)
            }}
          />
        </div>
      ) : (
        askVisible && (
          <div className={styles.seated} ref={askRef} id={OWNER_ASK_ID}>
            <p className={styles.seatedEyebrow}>{payoff.chips.join(' · ')}</p>
            <p className={styles.seatedTitle}>{tileHeadline(ownersSummary)}</p>
            {offer}
            <p className={styles.seatedText}>{gated ? GATED_FORM_COST_LINE : FORM_COST_LINE}</p>
            <p className={styles.seatedLinks}>
              <button
                type="button"
                className={styles.exampleToggle}
                aria-expanded={exampleOpen}
                onClick={() => setExampleOpen(isOpen => !isOpen)}
              >
                {exampleOpen ? EXAMPLE_TOGGLE.open : EXAMPLE_TOGGLE.closed}
              </button>
            </p>
            {exampleOpen && <OwnerExample />}
          </div>
        )
      )}
      <div className={styles.bandSlot} ref={slotRef} />
    </div>
  )
}

// ── the pick's block: both variants ───────────────────────────────────────────

/**
 * Chips, the action row, and what the row opens — under a roster row (`attached`) and under an
 * in-depth card (its own bordered block, once declared headed by the verdict sentence). One
 * component, so the two places a pick appears on the page say the same thing the same way
 * (Guillaume, 2026-09-17).
 */
const PickStrip: React.VFC<{
  pick: DSGuidePickRef
  /** Where the strip sits — the key into `stripRules` (`strip-rules.ts`). */
  placement: 'roster' | 'section' | 'grid'
  /** A section strip whose pick also has a roster row on the page (`DSNCOwnerGuidePick.hasRoster`). */
  hasRoster?: boolean
  /** A section strip that is its `nc-deals-large` card's footer (`DSNCOwnerGuidePick.inCard`). */
  inCard?: boolean
  declared: DeclaredProduct | null
  /** This pick IS the product the reader declared. */
  yours: boolean
}> = ({ pick, placement, hasRoster = false, inCard = false, declared, yours }) => {
  // Where the row opens (2026-10-18): the page's one side panel at `lg` and up, the pick's
  // sheet below it — the grid card and the in-depth card alike. Until the breakpoints are known
  // (the server, the first client render) the answer is the panel, so hydration agrees with
  // the HTML. Outside the lab provider there is no panel: the sheet at every width.
  const responsive = ResponsiveContext.useOptionalContainer()
  const lab = useLabCompare()
  const small = responsive?.ready === true && !responsive.lg
  const route: 'panel' | 'sheet' = small || lab === null ? 'sheet' : 'panel'
  const phone = route === 'sheet'
  // The page's scope — a guide hosts the ask, a review post has its own crawlers' copy — and
  // everything that differs per placement, from the one table.
  const pageType = useCatalogEvent().scope?.page_type ?? null
  const { context } = pick
  const rules: StripRules = stripRules({ placement, context, hasRoster, inCard, pageType })
  const grid = rules.shell === 'gridRow'
  // This block's claim on the page's one seat (`useSeatedOwner`).
  const seatToken = useRef(`seat-${Math.random().toString(36).slice(2)}`)
  const seatOwner = useSeatedOwner()
  const name = context?.name ?? pick.title
  const nodeId = context?.node_id ?? ''
  const theirs = useMemo(() => pickNumbers(pick), [pick])
  // The chip row: built for the sheet's "This pick" (`sheetChips`); printed on the strip only
  // where the table says (`chips` — nowhere since 2026-10-21).
  const chips = useMemo(
    () => (yours || !rules.sheetChips ? [] : rosterChips(theirs, declared)),
    [theirs, declared, yours, rules.sheetChips]
  )
  const trackOwner = useOwnerTrack({ nodeId, name })
  const owners = useMemo(() => cardOwnersFromCard(context?.owners), [context])
  const ownersCount = owners?.count ?? 0

  // Phone: the sheet, on one of its three segments (the row opens it on 'measure' or 'owners').
  const [sheet, setSheet] = useState<PickSheetSegment | null>(null)
  // Crossing `lg` with the sheet open unmounts it (its scroll lock restores); forget it too, or
  // it would reopen on the way back.
  useEffect(() => {
    if (!phone) {
      setSheet(null)
    }
  }, [phone])
  // Desktop: is the page's panel on THIS block's pick right now? (The same pick's roster row and
  // in-depth card are two blocks; only the one that opened the panel shows it pressed.)
  const focus = lab?.focus ?? null
  const focusedHere =
    focus !== null &&
    focus.placement === placement &&
    focus.pick.clearBuyProductId === pick.clearBuyProductId &&
    lab?.drawerOpen === true

  // The pick's curves load as the block scrolls in, not on open: `lab_tested` can claim curves
  // the lab then 404s (Shokz OpenRun Pro 2, 2026-10-16), and only the answer can take the
  // button away before the reader taps it. Never on mount — the observer arms as it scrolls in
  // (Directive `f80d2e70`); `curves.ts` caches per id, so opening costs no second request.
  const ref = useRef<HTMLDivElement>(null)
  const theirLabId = theirs.clearbuyProductId ?? null
  const wants = wantsCurves(declared, yours, context) && theirLabId !== null
  const inView = useInView(ref, wants)
  const curves = useCurves(theirLabId, wants && inView)
  // The verdict sentence is the in-depth block's: it needs both curves for its band clause,
  // fetched as the block comes into view.
  // No `rowIndex`: the disclosure is the roster's first row, never a card (Directive `21f605aa`).
  const pair = usePairVerdict({
    ref,
    declared,
    theirs,
    // Not gated on a lab id: below the lab tier the sentence is scores and specs.
    enabled: rules.verdict && declared !== null && !yours,
  })

  // The button is offered while the lab may have this pick, and goes once it answers "nothing".
  const measurable = wants && curves !== null
  // Opened before the answer came, and the answer is "nothing": close what was opened — the
  // sheet's Measurements segment (its Owners if it has any, else itself), or the panel's.
  // The sheet's half runs on the answer alone: since the sheet's segments are never disabled
  // (2026-10-19) a reader can choose "This pick" after the answer and read its empty state —
  // a pin (a new `lab` value) must not then move them off it.
  useEffect(() => {
    if (curves === null) {
      setSheet(current => {
        if (current !== 'measure') {
          return current
        }
        return ownersCount > 0 ? 'owners' : null
      })
    }
  }, [curves, ownersCount])
  // The panel's half, the same rule (2026-10-19): it runs when the answer arrives, never when
  // the reader later picks "This pick" on a pick the lab has nothing for — that click opens
  // the segment's empty state and stays there.
  const panelFocus = useRef({ focusedHere, segment: focus?.segment, lab })
  panelFocus.current = { focusedHere, segment: focus?.segment, lab }
  useEffect(() => {
    if (curves === null) {
      const { focusedHere: here, segment, lab: labNow } = panelFocus.current
      if (here && segment === 'measure' && labNow) {
        if (ownersCount > 0) {
          labNow.setSegment('owners')
        } else {
          labNow.clearFocus()
        }
      }
    }
  }, [curves, ownersCount])
  const verdict = rules.verdict && declared !== null && !yours && pair.clauses.length > 0
  // Under the verdict the chips would repeat its numbers (Guillaume, 2026-10-07: "I see
  // duplicates"), so the row is never built beside the sentence — the sheet has the sentence's
  // numbers in its own segments.
  const showChips = chips.length > 0 && !verdict
  // The one ask on the page: the owned pick's roster row, or its in-depth block when no row.
  const seats = yours && rules.seat && (seatOwner === null || seatOwner === seatToken.current)
  if (!rules.render) {
    // No lab curves and no owner review (or no context at all): no strip, on any placement
    // (Guillaume, 2026-10-21) — the row or card stands as the article wrote it. After the
    // hooks, never before them.
    return null
  }

  // The panel overlays the reader's curve — unless this IS their product, drawn alone.
  const overlay = yours ? null : declared
  const measureOpen = phone ? sheet === 'measure' : focusedHere && focus?.segment === 'measure'
  const measureSub: string | null = overlay ? ACTIONS.measurementsVs : null
  const { location } = rules
  const onMeasure = (): void => {
    trackOwner('lab_open', { lab_tab: 'freq', location })
    if (phone || !lab) {
      setSheet('measure')
      return
    }
    lab.focusPick(pick, placement, 'measure')
  }
  // Owners opens the panel (desktop) or the sheet (phone) — never a block in place.
  const onOwners = (): void => {
    if (phone || !lab) {
      trackOwner('notes_click', { location: `${location}_sheet`, item_count: ownersCount })
      setSheet('owners')
      return
    }
    trackOwner('notes_click', { location: `${location}_panel`, item_count: ownersCount })
    lab.focusPick(pick, placement, 'owners')
  }
  const ownersShown = phone ? sheet === 'owners' : focusedHere && focus?.segment === 'owners'
  const chipRow = showChips && (
    <p className={styles.chips}>
      {chips.map(chip => (
        <Chip
          key={chip.key}
          label={chip.label}
          theirs={chip.theirs}
          mine={chip.mine}
          max={chip.max}
        />
      ))}
    </p>
  )

  const shell = styles[rules.shell]
  // The crawlers' copy: the pick's reviews as the panel shows them, `hidden` — never shown by
  // the UI, never un-hidden (the Owners tile opens the panel / the sheet). One strip per pick
  // serves it (`servesOwners`). Items only: without them the panel shows the summary line
  // alone, and there is no review text to serve.
  const crawlOwners = rules.servesOwners && owners !== null && owners.items.length > 0
  return (
    <div className={clsx(shell, yours && rules.ownedLine && styles.owned)} ref={ref}>
      {verdict && declared && (
        <div className={styles.verdictHead}>
          <div className={styles.head}>
            <h3 className={styles.heading}>{`Against your ${declared.name}`}</h3>
            {/* The pick leads the key: on a guide the pick is the subject and the reader's
                product is the yardstick. Orange is still the reader's, on every surface
                (Constraint `f80d2e70`). */}
            <span className={styles.key} aria-hidden="true">
              <span className={styles.keyItem}>
                <span className={styles.swatch} style={{ backgroundColor: THIS_COLOR }} />
                <span className={styles.keyName}>{name}</span>
              </span>
              <span className={styles.keyItem}>
                <span className={styles.swatch} style={{ backgroundColor: YOURS_COLOR }} />
                <span className={styles.keyRole}>{'yours'}</span>
              </span>
            </span>
          </div>
          <Clauses clauses={pair.clauses} className={styles.verdict} />
        </div>
      )}
      {yours && declared && rules.ownedLine && (
        // Not a comparison: the "You own this" tag and the same fingerprint the reference
        // strip prints (their numbers, in orange).
        <div className={styles.rosterYours}>
          <span className={styles.ownedTag}>{GUIDE_COPY.invite.tag}</span>
          <DeclaredFingerprint declared={declared} />
        </div>
      )}
      <div className={grid ? undefined : styles.roster}>
        {rules.chips && chipRow}
        <PickActions
          pick={pick}
          placement={placement}
          yours={yours}
          measurable={measurable}
          measureOpen={measureOpen}
          measureSub={measureSub}
          onMeasure={onMeasure}
          ownersCount={ownersCount}
          ownersOpen={ownersShown}
          onOwners={onOwners}
          compact={grid}
        />
      </div>
      {seats && placement !== 'grid' && (
        <OwnedAsk pick={pick} placement={placement} owner={seatToken.current} />
      )}
      {owners && crawlOwners && (
        <div hidden data-owners-crawl={pick.clearBuyProductId}>
          <PickOwners
            owners={owners}
            claimedHours={context?.compare.batteryHours ?? null}
            pdpHref={context?.sg_href ?? null}
            nodeId={nodeId}
            productName={name}
            surface="card"
            crawl
          />
        </div>
      )}
      {sheet && (
        <PickSheet
          pick={pick}
          placement={placement}
          segment={sheet}
          onSegment={setSheet}
          onClose={() => setSheet(null)}
          chips={rules.sheetChips && chipRow}
          measurable={measurable}
          curves={curves}
          declared={overlay}
          yours={yours}
          owners={owners}
          trackOwner={trackOwner}
        />
      )}
    </div>
  )
}

const GuidePick: React.VFC<{ data: DSNCOwnerGuidePick }> = ({ data }) => {
  const { pick, variant } = data
  const declared = useDeclaredContext()?.declared ?? null
  const yours =
    declared !== null &&
    isDeclaredPick(
      declared,
      pick.context,
      pick.context?.compare.clearbuyProductId ?? pick.clearBuyProductId
    )
  return (
    <PickStrip
      pick={pick}
      placement={variant}
      hasRoster={data.hasRoster === true}
      inCard={data.inCard === true}
      declared={declared}
      yours={yours}
    />
  )
}

/**
 * The "Our picks" grid card's row (the new best-list style, 2026-10-17): the same three slots,
 * compact, under the card's buttons — `BestListCard`'s `footerSlot`, seated per card by ClearBuy
 * id (`gridActionsSlot`). A card with no id, or one the body's deal blocks do not carry, gets
 * none — the grid is the ClearBuy metabox's list and may name a product the article has no
 * section (and no lab context) for. The pin on the card's photo (`gridPinSlot`, 2026-10-17, one
 * round) is gone: the row has it.
 */
export const GuideGridPick: React.VFC<{ pick: DSGuidePickRef }> = ({ pick }) => {
  const declared = useDeclaredContext()?.declared ?? null
  const yours =
    declared !== null &&
    isDeclaredPick(
      declared,
      pick.context,
      pick.context?.compare.clearbuyProductId ?? pick.clearBuyProductId
    )
  return <PickStrip pick={pick} placement="grid" declared={declared} yours={yours} />
}

export function gridActionsSlot(
  picks: readonly DSGuidePickRef[]
): (card: Pick<DSBest, 'clearBuyProductId'>) => React.ReactNode {
  const byId = new Map(picks.map(pick => [pick.clearBuyProductId, pick]))
  // eslint-disable-next-line react/display-name -- the card's slot callback, not a component
  return card => {
    const pick = card.clearBuyProductId === null ? undefined : byId.get(card.clearBuyProductId)
    // The gate before the slot: a null slot leaves the card without its footer row.
    return pick && stripRenders(pick.context) ? <GuideGridPick pick={pick} /> : null
  }
}

export default GuidePick
